import { createSelector } from '@reduxjs/toolkit';
import type {
  TimelyPresentableAgentInterval,
  TimelyPresentableAgentResponse,
} from '@timelyapp/integration-cloud-api-spec';
import { EMPTY_ARRAY } from 'constants/defaults';
import { DateTime } from 'luxon';
import { getTagEntities } from 'redux/modules/tags.selectors';
import { getCurrentAccountId } from 'redux/selectors/accounts';
import { getEnrichedProjectsForCurrentUserById } from 'redux/selectors/projects';
import { getUserId } from 'redux/selectors/user';
import { isTicPredictionValidAsHour } from 'utils/tic/tic-prediction-valid-as-hour';
import { notNull } from 'utils/typesafe';
import { getTicApiSlice } from '../api-slice/tic.api.selector';
import { ticApi } from '../api-slice/tic.apiSlice';
import { getAiResponse } from './tic.ai.agent.slice';

export const getAssistantResponseQuery = createSelector(
  [getCurrentAccountId],
  timely_account_id => {
    return {
      timelyAccountId: timely_account_id,
      message: '',
    };
  }
);

export const getTicAgentResponseRTKQueryBase = createSelector(
  [getTicApiSlice, getAssistantResponseQuery],
  (localState, query) => {
    return ticApi.endpoints.getAssistantResponse.select(query)({
      [ticApi.reducerPath]: localState,
    });
  }
);

export const getPresentableAgentResponseWithIntervals = createSelector(
  [getAiResponse, getEnrichedProjectsForCurrentUserById, getTagEntities, getUserId],
  (
    aiResponse,
    enrichedProjectForUserIdMap,
    tagEntities,
    userId
  ): TimelyPresentableAgentResponse => {
    if (!aiResponse) {
      return {
        timely_account_id: 0,
        timely_user_id: 0,
        infered_dates: [],
        response_text: '',
        total_seconds: 0,
        dates_to_intervals_map: {},
        num_projects: 0,
        num_intervals: 0,
      };
    }

    const intervals = aiResponse.intervals;
    const datesToIntervalsMap: Record<string, TimelyPresentableAgentInterval[]> = {};

    if (intervals) {
      intervals.forEach(interval => {
        const date = DateTime.fromISO(interval.started_at).toFormat('yyyy-MM-dd');
        const prediction = interval.predictions[0];
        const projectRef = enrichedProjectForUserIdMap[prediction.timely_project_id];
        const project = projectRef?.fullProject || projectRef?.base || null;

        const labels = (prediction?.timely_label_ids || EMPTY_ARRAY)
          .map(labelId => {
            const tag = tagEntities[labelId];
            if (!tag) return null;
            return { id: tag.id, name: tag.name, emoji: tag.emoji };
          })
          .filter(notNull);

        const transformedInterval = {
          ...interval,
          note: prediction.note || '',
          valid_as_hour: prediction.valid_as_hour,
          total_seconds: DateTime.fromISO(interval.ended_at)
            .diff(DateTime.fromISO(interval.started_at))
            .as('seconds'),
          color: !!project && 'color' in project ? `#${project.color}` : 'rgba(0, 0, 0, 0.1)',
          project,
          labels: labels,
          prediction: {
            timely_project_id: prediction.timely_project_id,
            timely_label_ids: prediction.timely_label_ids,
            confidence: prediction.confidence,
          },
          inferred: prediction,
        };

        const validAsHour = isTicPredictionValidAsHour({
          prediction: transformedInterval,
          project,
          userId,
        });
        transformedInterval.valid_as_hour = validAsHour;
        datesToIntervalsMap[date] = [...(datesToIntervalsMap[date] || []), transformedInterval];
      });
    }

    return {
      timely_account_id: aiResponse.timely_account_id,
      timely_user_id: aiResponse.timely_user_id,
      infered_dates: aiResponse.infered_dates,
      response_text: aiResponse.response_text,
      response_type: aiResponse.response_type,
      total_seconds:
        intervals?.reduce(
          (acc, interval) =>
            acc +
            DateTime.fromISO(interval.ended_at)
              .diff(DateTime.fromISO(interval.started_at))
              .as('seconds'),
          0
        ) || 0,
      dates_to_intervals_map: datesToIntervalsMap,
      num_projects: Object.keys(datesToIntervalsMap).length,
      num_intervals: intervals?.length || 0,
    };
  }
);

// Example response until we get proper typings
// {
//   "timely_account_id": 708369,
//   "timely_user_id": 2275600,
//   "response_type": "intervals",
//   "event": "request_processed",
//   "range_started_at": "2025-01-20T00:00:00+01:00",
//   "range_ended_at": "2025-01-20T23:59:59.999999+01:00",
//   "infered_dates": [
//       "2025-01-20"
//   ],
//   "response_text": "I've logged all your time spent in Cursor today to the Timesheet Assistant 2.0 project.",
//   "intervals": [
//       {
//           "started_at": "2025-01-20T09:32:00+01:00",
//           "ended_at": "2025-01-20T11:43:00+01:00",
//           "predictions": [
//               {
//                   "timely_project_id": 4223913,
//                   "timely_label_ids": [],
//                   "confidence": 1,
//                   "note": "",
//                   "valid_as_hour": true
//               }
//           ]
//       },
//       {
//           "started_at": "2025-01-20T13:31:00+01:00",
//           "ended_at": "2025-01-20T15:01:00+01:00",
//           "predictions": [
//               {
//                   "timely_project_id": 4223913,
//                   "timely_label_ids": [],
//                   "confidence": 1,
//                   "note": "",
//                   "valid_as_hour": true
//               }
//           ]
//       }
//   ]
// }
